import { useTranslation } from 'react-i18next';
import { Check, Search, Truck, ShieldCheck, Gift, FileText } from 'lucide-react';

export default function WhatIs() {
  const { t } = useTranslation();

  const features = [
    {
      icon: Search,
      label: 'Product Research',
      desc: 'Find verified products across 14+ categories',
      image: 'https://readdy.ai/api/search-image?query=Person%20researching%20fashion%20products%20on%20a%20laptop%20with%20printed%20product%20cards%20and%20handwritten%20notes%20spread%20on%20a%20bright%20desk%2C%20soft%20natural%20lighting%2C%20editorial%20productivity%20photography%2C%20clean%20organized%20scene&width=400&height=300&seq=feature-research-301&orientation=landscape',
    },
    {
      icon: Truck,
      label: 'Shipping Guides',
      desc: 'Compare shipping methods and costs by country',
      image: 'https://readdy.ai/api/search-image?query=Global%20shipping%20and%20logistics%20concept%20with%20small%20cardboard%20boxes%20and%20a%20world%20map%20on%20a%20desk%2C%20soft%20natural%20lighting%2C%20modern%20business%20editorial%20photography%2C%20muted%20blue%20tones&width=400&height=300&seq=feature-shipping-302&orientation=landscape',
    },
    {
      icon: ShieldCheck,
      label: 'QC & Safety',
      desc: 'Quality control tips and legit checks',
      image: 'https://readdy.ai/api/search-image?query=Quality%20inspection%20of%20a%20folded%20clothing%20item%20with%20a%20magnifying%20glass%20and%20checklist%20on%20a%20clean%20white%20table%2C%20bright%20studio%20lighting%2C%20professional%20editorial%20photography%2C%20precise%20detail&width=400&height=300&seq=feature-qc-303&orientation=landscape',
    },
    {
      icon: Gift,
      label: 'Coupons & Deals',
      desc: 'Latest discount codes and promo offers',
      image: 'https://readdy.ai/api/search-image?query=Stack%20of%20discount%20coupons%20and%20a%20gift%20card%20beside%20a%20small%20calculator%20on%20a%20warm%20wooden%20desk%2C%20soft%20lighting%2C%20savings%20concept%20editorial%20photography%2C%20rich%20warm%20tones&width=400&height=300&seq=feature-coupon-304&orientation=landscape',
    },
    {
      icon: FileText,
      label: 'Buying Guides',
      desc: 'Step-by-step guides for beginners',
      image: 'https://readdy.ai/api/search-image?query=Open%20guidebook%20with%20fashion%20photos%20and%20a%20shopping%20bag%20on%20a%20clean%20bright%20desk%2C%20soft%20natural%20light%2C%20lifestyle%20editorial%20photography%2C%20warm%20inviting%20mood&width=400&height=300&seq=feature-guides-305&orientation=landscape',
    },
  ];

  const trustItems = [
    t('trust.item1'),
    t('trust.item2'),
    t('trust.item3'),
    t('trust.item4'),
  ];

  return (
    <section id="review" className="relative w-full py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-6 lg:px-8 xl:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="animate-fade-in-up">
              <span className="text-sm font-semibold text-primary-600 tracking-wider uppercase mb-3 block">
                About
              </span>
              <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-5 leading-tight">
                {t('whatIs.title')}
              </h2>
              <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-6">
                {t('whatIs.description')}
              </p>

              <div className="space-y-3">
                {trustItems.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-primary-600" />
                    </div>
                    <span className="text-foreground-700 text-sm md:text-base">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className={`group rounded-2xl bg-background-100 border border-background-200/60 hover:border-primary-300/50 hover:bg-background-50 transition-all duration-300 overflow-hidden ${
                    index === 0 ? 'sm:col-span-2' : ''
                  }`}
                  style={{ animationDelay: `${index * 0.08}s` }}
                >
                  <div className="relative w-full aspect-[16/9] overflow-hidden">
                    <img
                      src={feature.image}
                      alt={feature.label}
                      title={`${feature.label} — AllChinaBuy Spreadsheet`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/40 to-transparent" />
                  </div>
                  <div className="p-4 md:p-5">
                    <div className="flex items-center gap-2.5 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center group-hover:bg-primary-100 transition-colors">
                        <feature.icon className="w-4 h-4 text-primary-600" />
                      </div>
                      <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900">
                        {feature.label}
                      </h3>
                    </div>
                    <p className="text-foreground-600 text-xs md:text-sm leading-relaxed">
                      {feature.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}